import type { LucideIcon } from "lucide-react";
import type { ComponentProps, FC, ReactNode } from "react";

import { cn } from "@/lib/utils";

type HeaderProps = {
  title: ReactNode;
  /** Inline 14px icon before the title. */
  icon?: LucideIcon;
  /** Shown as a small meta count after the title when a number. */
  count?: number | null;
  /** Right-aligned controls. */
  actions?: ReactNode;
  className?: string;
};

/**
 * The 36px title-case header band of a `Surface`: 13px weight 500 with an
 * optional inline icon and a muted count, split from the body by a `line-1`
 * divider. The uppercase style is reserved for `PaneHeader`.
 */
export const SurfaceHeader: FC<HeaderProps> = ({
  title,
  icon: Icon,
  count,
  actions,
  className
}) => (
  <header
    data-slot="surface-header"
    className={cn(
      "flex h-9 shrink-0 items-center gap-2 border-b border-line-1 px-4",
      className
    )}
  >
    {Icon ? <Icon className="size-3.5 shrink-0 text-fg-3" aria-hidden /> : null}
    <span className="min-w-0 truncate text-base font-medium text-fg">
      {title}
    </span>
    {typeof count === "number" ? (
      <span
        className="text-base text-fg-3 tabular-nums"
        data-testid="card-count"
      >
        {count}
      </span>
    ) : null}
    {actions ? (
      <span className="ml-auto flex shrink-0 items-center gap-0.5">
        {actions}
      </span>
    ) : null}
  </header>
);

type SurfaceProps = ComponentProps<"div"> & {
  /** Drop the outer ring and radius (the container already supplies one). */
  embedded?: boolean;
};

/**
 * The one outlined container per stack: elevated, `ring-1 ring-line-2`,
 * `rounded-xl`, padding 0 (rows supply their own). Put a `SurfaceHeader`
 * first and the content after it.
 */
export const Surface: FC<SurfaceProps> = ({
  embedded = false,
  className,
  ...props
}) => (
  <div
    data-slot="surface"
    data-embedded={embedded || undefined}
    {...props}
    className={cn(
      "flex min-w-0 flex-col overflow-hidden bg-elevated text-fg",
      !embedded && "rounded-xl ring-1 ring-line-2",
      className
    )}
  />
);
